/**
 * Arsenal de scripts de prospecção ativa (Outbound).
 * Conteúdo curado dos estudos Full Sales (ver memory/comercial/13 e 14).
 * Usado tanto no painel "Outbound" (estudo + métricas) quanto na aba Outbound do card do funil.
 *
 * Estrutura: categoria -> fluxo -> etapa (stage) -> scripts.
 * Etapas do tipo 'decision' (ex.: "Respondeu?") não têm scripts: registram um desfecho (sim/não).
 * Scripts da etapa "Desenvolvimento" carregam `part`: 'problema' | 'rota' | 'solucao'.
 */

// ─── Blocos de Desenvolvimento reaproveitados (PROBLEMA / ROTA / SOLUÇÃO) ───
// A metodologia Full Sales: expor o PROBLEMA (pergunta que dói), mostrar a ROTA
// (implicação/o que a dor custa) e então a SOLUÇÃO (o pit da reunião).
// Ângulo IA / Product-Market Fit: "IA já é atraente pro mercado, o simples bem feito
// funciona". Diagnóstico de maturidade em IA + posicionamento como IA sob medida nos
// processos (não um "ChatGPT da vida"). Reaproveitado em ligação e chat.
const DESENVOLVIMENTO_IA = [
  {
    id: 'dev_ia_diagnostico',
    part: 'problema',
    title: 'Diagnóstico de IA (pergunta aberta)',
    objective: 'Abrir a conversa sobre IA sem pitch — deixar o dono se posicionar.',
    body: 'Deixa eu te perguntar, {{nome}}: o que a {{empresa}} tem usado hoje de soluções de Inteligência Artificial?',
  },
  {
    id: 'dev_ia_chatgpt',
    part: 'problema',
    title: 'ChatGPT genérico x IA no processo',
    objective: 'Expor a lacuna entre "usar ChatGPT" e ter IA dentro da operação.',
    body: 'E os seus funcionários estão usando só um "ChatGPT da vida", cada um por conta, ou você já tem IA implementada dentro dos processos da empresa?',
  },
  {
    id: 'dev_ia_rota',
    part: 'rota',
    title: 'O custo de ficar no ChatGPT genérico',
    objective: 'Mostrar o que se perde ao não ter IA no processo.',
    body: 'A diferença é grande: ChatGPT solto é cada um por conta e some quando a pessoa sai. IA dentro do processo é a empresa toda ganhando — atendimento que não dorme, follow-up que não esquece, a tarefa repetitiva que some. Quem fica só no genérico deixa a maior parte do ganho na mesa.',
  },
  {
    id: 'dev_ia_solucao',
    part: 'solucao',
    title: 'IA sob medida nos processos (pit)',
    objective: 'Posicionar a SKY como IA sob medida que entra na operação — não um chatbot.',
    body: 'É exatamente isso que a gente faz: cria soluções de IA sob medida pra {{empresa}}, que entram nos seus processos — atendimento, qualificação, follow-up, o que fizer mais sentido — e trabalham 24 horas. Na reunião eu te mostro, no seu cenário, onde a IA dá mais resultado primeiro. A gente já faz isso pra {{autoridade}}.',
  },
];

const DESENVOLVIMENTO_LIGACAO = [
  {
    id: 'dev_call_problema',
    part: 'problema',
    title: 'Pergunta que expõe a dor',
    objective: 'Fazer o dono sentir a perda antes de qualquer pitch. Pergunte e cale a boca.',
    body: '{{nome}}, deixa eu te perguntar uma coisa rápida: quando um cliente chama a {{empresa}} no WhatsApp fora do horário, num domingo ou de madrugada, quem responde? … E em quanto tempo?',
  },
  {
    id: 'dev_call_rota',
    part: 'rota',
    title: 'Implicação — a conta da perda',
    objective: 'Transformar a dor em dinheiro perdido, sem exagero.',
    body: 'Pois é. O cliente te acha, chama animado, e se demora pra responder ele já foi no concorrente. A maioria dos donos perde de 3 a 5 vendas por semana só nisso, sem perceber — é o lead que ninguém respondeu e ninguém foi atrás depois.',
  },
  {
    id: 'dev_call_solucao',
    part: 'solucao',
    title: 'Solução — o pit da reunião',
    objective: 'Apresentar o resultado concreto que ele leva da reunião + autoridade.',
    body: 'O que eu quero te mostrar em 15 minutos é um funcionário de IA atendendo esse WhatsApp por você: responde em segundos, 24 horas, qualifica e corre atrás sozinho de quem sumiu. Na reunião eu faço a conta com os seus números — quanto você provavelmente perde hoje. A gente já faz isso pra {{autoridade}}.',
  },
  ...DESENVOLVIMENTO_IA,
];

const DESENVOLVIMENTO_DM = [
  {
    id: 'dev_dm_problema',
    part: 'problema',
    title: 'Pergunta de gap (no chat)',
    objective: 'Descobrir a dor pela conversa, sem pitch.',
    body: '{{nome}}, me conta uma coisa: hoje, quando cai uma mensagem nova pra {{empresa}} fora do horário comercial, o que acontece com ela? Alguém responde na hora ou fica pro dia seguinte?',
  },
  {
    id: 'dev_dm_rota',
    part: 'rota',
    title: 'Implicação — o lead esfria',
    objective: 'Mostrar o custo da demora com leveza.',
    body: 'É aí que escapa dinheiro. Você atrai o lead, ele manda mensagem quente, e se ninguém responde rápido ele compra de quem respondeu primeiro. E quase ninguém faz o follow-up de quem disse "depois eu vejo".',
  },
  {
    id: 'dev_dm_solucao',
    part: 'solucao',
    title: 'Solução — proposta indireta',
    objective: 'Plantar a solução sem empurrar; puxar para a call.',
    body: 'Tenho uma forma da {{empresa}} responder cada lead em segundos, 24h, qualificando e agendando sozinho — sem contratar mais gente. Não dá pra te mostrar bem por aqui, é visual. Faz sentido eu te mostrar em 15 minutos numa call?',
  },
  ...DESENVOLVIMENTO_IA,
];

export const OUTBOUND_PLAYBOOK = [
  {
    id: 'cold_call',
    label: 'Cold Call',
    icon: 'i-lucide-phone-call',
    description:
      'Ligação ativa para o dono que não te conhece. Os primeiros 15 segundos decidem: nome + gancho real antes de dizer o que vende.',
    flows: [
      {
        id: 'atendeu',
        label: 'Atendeu a ligação',
        description: 'O dono atendeu. Abra com gancho, peça 2 minutos e conduza até o compromisso.',
        stages: [
          {
            id: 'abordagem',
            label: 'Abordagem',
            type: 'script',
            hint: 'Nome + gancho hiperpersonalizado + pedido de 2 minutos.',
            scripts: [
              {
                id: 'cc_ab_google',
                title: 'Gancho pelas avaliações do Google',
                objective: 'Provar em uma frase que a ligação é sobre o negócio DELE.',
                body: '{{nome}}? Oi, aqui é o {{consultor}}, da SKY. Eu estava agora vendo a {{empresa}} no Google, {{nota_google}}, e chamou minha atenção. Foi por isso que te liguei. Me dá dois minutos? Se não fizer sentido, eu mesmo encerro.',
              },
              {
                id: 'cc_ab_dor',
                title: 'Gancho direto na dor',
                objective: 'Abrir com uma pergunta que já mexe na ferida.',
                body: '{{nome}}? Bom dia, é o {{consultor}}. Vou ser rápido: eu vi a {{empresa}} — {{gancho}} — e me bateu uma pergunta honesta. Se um cliente chamar no WhatsApp de vocês nesse minuto, em quanto tempo ele é respondido?',
              },
              {
                id: 'cc_ab_quebra',
                title: 'Quebra de padrão (anti-telemarketing)',
                objective: 'Desarmar com honestidade e ganhar 30 segundos.',
                body: '{{nome}}? Oi, é da SKY. Já vou ser honesto: é uma ligação que você não esperava. Mas eu não peguei número de lista — eu vi a {{empresa}}, {{gancho}}. Me dá 30 segundos e, se não fizer sentido, eu mesmo desligo. Pode ser?',
              },
              {
                id: 'cc_ab_ia',
                title: 'IA sob medida (simples e direto)',
                objective: 'O simples bem feito: IA já é atraente pro mercado. Vá direto, sem rodeio.',
                body: '{{nome}}, tudo bem? Aqui é o {{consultor}}. Eu trabalho com soluções de Inteligência Artificial feitas sob medida pra empresas. Posso falar rapidinho com você?',
              },
            ],
          },
          {
            id: 'desenvolvimento',
            label: 'Desenvolvimento',
            type: 'script',
            hint: 'PROBLEMA (pergunta que dói) → ROTA (o que a dor custa) → SOLUÇÃO (o pit).',
            scripts: DESENVOLVIMENTO_LIGACAO,
          },
          {
            id: 'cta',
            label: 'CTA',
            type: 'script',
            hint: 'Chamar para a reunião com 3 horários fechados. Nunca "qual horário você tem?".',
            scripts: [
              {
                id: 'cc_cta_3horarios',
                title: 'CTA — 3 horários fechados',
                objective: 'Fechar o agendamento com opções, não com pergunta aberta.',
                body: 'Não preciso de nada agora, só 15 minutos pra te mostrar funcionando. Eu tenho {{horario_1}}, {{horario_2}} ou {{horario_3}}. Qual encaixa melhor pra você?',
              },
              {
                id: 'cc_cta_nivel3',
                title: 'CTA nível 3 — "impossível não agendar"',
                objective: 'Tirar o peso da call comercial e tornar o não constrangedor.',
                body: 'Olha, {{nome}}, vou ser transparente: é uma conversa comercial, sim. Mas mesmo que você não queira nada, sai com {{resultado}} na mão. Me dá esse {{horario_1}} aqui?',
              },
              {
                id: 'cc_cta_ia',
                title: 'CTA — diagnóstico de IA',
                objective: 'Oferecer um diagnóstico rápido como isca de baixa fricção.',
                body: 'Que tal a gente fazer um diagnóstico rápido de IA da {{empresa}}? Uns 20 minutos e eu te mostro onde dá pra aplicar primeiro. Tenho {{horario_1}}, {{horario_2}} ou {{horario_3}} — qual encaixa?',
              },
            ],
          },
          {
            id: 'compromisso',
            label: 'Compromisso',
            type: 'script',
            hint: 'Confirmar o horário (anti no-show), pegar o WhatsApp e mandar o convite na hora.',
            scripts: [
              {
                id: 'cc_comp_confirma',
                title: 'Confirmação anti no-show + convite',
                objective: 'Travar o compromisso na agenda do lead.',
                body: 'Fechado, {{nome}}. Fica {{horario_1}}, então. Esse número que eu liguei é o melhor pra eu te mandar o convite? Perfeito — te mando agora e, no dia, te dou um toque rápido só pra garantir. A gente se fala {{horario_1}}.',
              },
            ],
          },
        ],
      },
      {
        id: 'nao_atendeu',
        label: 'Não atendeu a ligação',
        description: 'Caiu na caixa ou não atendeu. Aquece por WhatsApp e traz de volta pra ligação.',
        stages: [
          {
            id: 'whatsapp',
            label: 'Mensagem WhatsApp',
            type: 'script',
            hint: 'Recado curto com gancho e ponte pro retorno. Não vender no texto.',
            scripts: [
              {
                id: 'cc_wa_ponte',
                title: 'WhatsApp após não atender',
                objective: 'Dar rosto ao número desconhecido e abrir a conversa.',
                body: 'Oi {{nome}}, é o {{consultor}}, da SKY — acabei de tentar te ligar. Nada urgente: eu vi {{gancho}} na {{empresa}} e tenho uma ideia rápida pra vocês pararem de perder cliente fora do horário. Me chama por aqui quando puder, ou me diz um horário bom pra eu retornar.',
              },
              {
                id: 'cc_wa_curiosidade',
                title: 'WhatsApp com loop de curiosidade',
                objective: 'Gerar resposta com uma pergunta em aberto.',
                body: '{{nome}}, é o {{consultor}}. Liguei porque vi as avaliações da {{empresa}} no Google e fiquei com uma pergunta rápida sobre o atendimento de vocês no WhatsApp. Não é venda. Me retorna aqui quando puder que é rapidinho.',
              },
              {
                id: 'cc_wa_ia',
                title: 'IA sob medida (WhatsApp)',
                objective: 'Abrir pelo tema de IA, que já desperta interesse por si só.',
                body: 'Oi {{nome}}, aqui é o {{consultor}} — acabei de tentar te ligar. Eu trabalho com soluções de Inteligência Artificial feitas sob medida pra empresas como a {{empresa}}. Tenho uma ideia rápida que pode encaixar aí. Quando você tem 2 minutos?',
              },
            ],
          },
          {
            id: 'respondeu',
            label: 'Respondeu?',
            type: 'decision',
            hint: 'Marque o desfecho: respondeu ou não. Se não, entra na cadência de follow-up.',
          },
          {
            id: 'desenvolvimento',
            label: 'Desenvolvimento',
            type: 'script',
            hint: 'PROBLEMA → ROTA → SOLUÇÃO, agora pelo chat.',
            scripts: DESENVOLVIMENTO_DM,
          },
          {
            id: 'cta_ligacao',
            label: 'CTA para ligação',
            type: 'script',
            hint: 'Puxar do texto para a ligação/reunião com horários.',
            scripts: [
              {
                id: 'cc_ctalig_1',
                title: 'CTA — trazer para a call',
                objective: 'Converter a conversa de texto em reunião marcada.',
                body: 'Isso rende muito mais em 15 minutos de call do que por texto, {{nome}} — te mostro com os números da {{empresa}}. Consigo {{horario_1}} ou {{horario_2}}. Qual fica melhor?',
              },
            ],
          },
          {
            id: 'compromisso',
            label: 'Compromisso',
            type: 'script',
            hint: 'Confirmar horário + convite.',
            scripts: [
              {
                id: 'cc_comp2_confirma',
                title: 'Confirmação + convite',
                objective: 'Travar o compromisso.',
                body: 'Fechado, {{nome}}. {{horario_1}}, então. Te mando o convite agora e um lembrete no dia. A gente se fala!',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'social_selling',
    label: 'Social Selling',
    icon: 'i-lucide-instagram',
    description:
      'Prospecção pelo Instagram/DM. Abertura personalizada pelo perfil, nunca pitch na primeira mensagem. O follow-up é o segredo.',
    flows: [
      {
        id: 'respondeu',
        label: 'Respondeu / engajou',
        description: 'O lead respondeu, seguiu, comentou ou curtiu. Conduza da apreciação ao agendamento.',
        stages: [
          {
            id: 'abordagem',
            label: 'Abordagem (DM)',
            type: 'script',
            hint: 'Apreciação sincera + razão da seleção + permissão. Nunca pitch na primeira.',
            scripts: [
              {
                id: 'ss_ab_perfil',
                title: 'Abertura pelo perfil',
                objective: 'Observação genuína que gera a resposta mínima viável.',
                body: 'Oi {{nome}}! Vi que você acompanha {{ponto_comum}}. Dei uma olhada na {{empresa}} e achei muito bom o que vocês fazem — foi por isso que resolvi te chamar. Posso te fazer uma pergunta rápida?',
              },
              {
                id: 'ss_ab_carteirada',
                title: 'Apreciação + carteirada de autoridade',
                objective: 'Plantar autoridade indireta enquanto elogia de verdade.',
                body: '{{nome}}, tudo bem? Sou o {{consultor}}, da SKY — a gente cuida do comercial de empresas como {{autoridade}}. Entrei no perfil da {{empresa}} e {{gancho}} me chamou atenção. Foi de propósito que te chamei, não é lista. Posso te pontuar uma coisa rápida?',
              },
              {
                id: 'ss_ab_ia',
                title: 'IA sob medida (DM)',
                objective: 'Puxar pelo tema de IA — atrativo e diferenciador.',
                body: 'Oi {{nome}}, tudo bem? Aqui é o {{consultor}}. Eu ajudo empresas a colocarem Inteligência Artificial de verdade dentro dos processos — não só um "ChatGPT da vida". Dei uma olhada na {{empresa}} e achei que fazia sentido te chamar. Posso te fazer uma pergunta rápida?',
              },
            ],
          },
          {
            id: 'desenvolvimento',
            label: 'Desenvolvimento',
            type: 'script',
            hint: 'PROBLEMA → ROTA → SOLUÇÃO no chat.',
            scripts: DESENVOLVIMENTO_DM,
          },
          {
            id: 'cta',
            label: 'CTA',
            type: 'script',
            hint: 'Propor a call com horários fechados.',
            scripts: [
              {
                id: 'ss_cta_call',
                title: 'CTA — 20 minutos de call',
                objective: 'Converter a conversa em reunião.',
                body: 'Posso te mostrar como funciona em uns 20 minutos, {{nome}}. Tenho {{horario_1}} ou {{horario_2}} — qual funciona melhor pra você?',
              },
              {
                id: 'ss_cta_ia',
                title: 'CTA — diagnóstico de IA',
                objective: 'Oferecer diagnóstico de IA como próximo passo leve.',
                body: 'Posso te mostrar num diagnóstico rápido de 20 minutos onde a IA encaixa primeiro na {{empresa}}, {{nome}}. Tenho {{horario_1}} ou {{horario_2}} — qual funciona?',
              },
            ],
          },
          {
            id: 'compromisso',
            label: 'Compromisso',
            type: 'script',
            hint: 'Confirmar + pegar WhatsApp + convite.',
            scripts: [
              {
                id: 'ss_comp_confirma',
                title: 'Confirmação + convite',
                objective: 'Travar o compromisso e mudar de canal pro WhatsApp.',
                body: 'Fechado, {{nome}}! {{horario_1}}. Me passa seu WhatsApp que eu já te mando o convite e um lembrete no dia. Até lá!',
              },
            ],
          },
        ],
      },
      {
        id: 'nao_respondeu',
        label: 'Não respondeu',
        description: 'Sem resposta na abordagem. A cadência de follow-up é onde estão as vendas.',
        stages: [
          {
            id: 'followup',
            label: 'Follow-up (nível 1)',
            type: 'script',
            hint: 'Toques leves, cada um com um ângulo novo. Nunca "viu minha mensagem?".',
            scripts: [
              {
                id: 'ss_fu_1',
                title: 'Follow-up nível 1',
                objective: 'Reabrir a conversa sem cobrar.',
                body: 'Oi {{nome}}, tudo bem? Fiquei com uma ideia rápida na cabeça sobre o atendimento da {{empresa}} — vale 30 segundos. Consigo te mostrar hoje ou amanhã?',
              },
              {
                id: 'ss_fu_audio',
                title: 'Follow-up com áudio/vídeo',
                objective: 'Personalizar em escala e provar que é gente de verdade.',
                body: 'Mandar um áudio curto (ou vídeo de 20s): "{{nome}}, gravei rapidinho só pra você ver que sou eu mesmo. Vi {{gancho}} na {{empresa}} e tenho uma ideia que encaixa certinho. Me chama quando puder!"',
              },
            ],
          },
          {
            id: 'respondeu',
            label: 'Respondeu?',
            type: 'decision',
            hint: 'Marque o desfecho: respondeu ou não.',
          },
          {
            id: 'desenvolvimento',
            label: 'Desenvolvimento',
            type: 'script',
            hint: 'PROBLEMA → ROTA → SOLUÇÃO.',
            scripts: DESENVOLVIMENTO_DM,
          },
          {
            id: 'cta_call',
            label: 'CTA para call',
            type: 'script',
            hint: 'Trazer para a reunião.',
            scripts: [
              {
                id: 'ss_ctacall_1',
                title: 'CTA — trazer para a call',
                objective: 'Converter em reunião com horários.',
                body: 'Isso rende muito mais numa call de 20 minutos do que por aqui, {{nome}}. Tenho {{horario_1}} ou {{horario_2}}. Qual encaixa?',
              },
            ],
          },
          {
            id: 'compromisso',
            label: 'Compromisso',
            type: 'script',
            hint: 'Confirmar + convite.',
            scripts: [
              {
                id: 'ss_comp2_confirma',
                title: 'Confirmação + convite',
                objective: 'Travar o compromisso.',
                body: 'Fechado, {{nome}}! {{horario_1}}. Te mando o convite e um lembrete no dia. Até lá!',
              },
            ],
          },
        ],
      },
    ],
  },
];

// ─── Helpers de lookup (usados pelo painel e pela aba do card) ───

export const findCategory = categoryId =>
  OUTBOUND_PLAYBOOK.find(c => c.id === categoryId) || null;

export const findFlow = (categoryId, flowId) => {
  const category = findCategory(categoryId);
  return category ? category.flows.find(f => f.id === flowId) || null : null;
};

export const findStage = (categoryId, flowId, stageId) => {
  const flow = findFlow(categoryId, flowId);
  return flow ? flow.stages.find(s => s.id === stageId) || null : null;
};

export const listStageScripts = (categoryId, flowId, stageId) => {
  const stage = findStage(categoryId, flowId, stageId);
  return stage && stage.scripts ? stage.scripts : [];
};

// Índice global id -> { script, categoryId, flowId, stageId } para rótulos em métricas/timeline.
const SCRIPT_INDEX = (() => {
  const index = {};
  OUTBOUND_PLAYBOOK.forEach(category => {
    category.flows.forEach(flow => {
      flow.stages.forEach(stage => {
        (stage.scripts || []).forEach(script => {
          // primeira ocorrência vence (o mesmo bloco pode aparecer em vários fluxos)
          if (!index[script.id]) {
            index[script.id] = {
              script,
              categoryId: category.id,
              flowId: flow.id,
              stageId: stage.id,
            };
          }
        });
      });
    });
  });
  return index;
})();

export const findScriptById = scriptId => SCRIPT_INDEX[scriptId] || null;

export const DEV_PARTS = [
  { id: 'problema', label: 'PROBLEMA', color: 'text-n-ruby-11' },
  { id: 'rota', label: 'ROTA', color: 'text-n-amber-11' },
  { id: 'solucao', label: 'SOLUÇÃO', color: 'text-n-teal-11' },
];
